import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useLabProtocol, useUpdateLabProtocolStatus, useCreateLabResult, useCreateLabResultsBulk, useUpdateLabResult, useDeleteLabResult, useLabReferenceValues, useSeedCanineReferenceValues } from '@/hooks/useLab';
import { LabProtocolStatus, LabTestType, LabResult } from '@/types';
import { LabResultCreate } from '@/services/lab';

// Safe render helper
function s(v: unknown): string {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

const testTypeLabels: Record<string, string> = {
  hemograma: 'Hemograma',
  bioquimica: 'Bioquimica',
  orina: 'Orina',
  coprologia: 'Coprologia',
  otros: 'Otros',
};

function testTypeLabel(tt: string): string {
  return testTypeLabels[tt] || tt;
}

export default function ProtocolDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const protocolId = id ? parseInt(id) : null;
  const pendingAction = useRef(searchParams.get('action'));

  const { data: protocol, isLoading, refetch } = useLabProtocol(protocolId);
  const { data: referenceValues = [] } = useLabReferenceValues({
    species: protocol?.patient?.species,
    is_active: true,
  });
  const updateStatusMutation = useUpdateLabProtocolStatus();
  const createResultMutation = useCreateLabResult();
  const createResultsBulkMutation = useCreateLabResultsBulk();
  const updateResultMutation = useUpdateLabResult();
  const deleteResultMutation = useDeleteLabResult();
  const seedCanineMutation = useSeedCanineReferenceValues();

  const printRef = useRef<HTMLDivElement>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [showAddResult, setShowAddResult] = useState(false);
  const [resultForm, setResultForm] = useState<LabResultCreate>({
    test_type: 'hemograma',
    parameter_code: '',
    parameter_name: '',
    result_value: '',
    result_numeric: undefined,
    reference_min: undefined,
    reference_max: undefined,
    unit: '',
    notes: '',
  });

  if (isLoading) return <div className="p-8 text-center">{"Cargando..."}</div>;
  if (!protocol) return <div className="p-8 text-center text-red-600">{"Protocolo no encontrado"}</div>;

  // All derived data as simple strings
  const protocolNumber = s(protocol.protocol_number);
  const protocolDate = (() => {
    try {
      const d = protocol.protocol_date;
      if (d == null) return '-';
      return new Date(String(d)).toLocaleDateString('es-AR');
    } catch { return s(protocol.protocol_date); }
  })();
  const statusText = s(protocol.status);
  const patientName = s(protocol.patient?.name);
  const patientSpecies = s(protocol.patient?.species);
  const ownerName = s(protocol.patient?.owner_name);
  const ownerPhone = s(protocol.patient?.owner_phone);
  const vetName = s(protocol.requesting_vet_name);
  const clinicName = s(protocol.patient?.referring_clinic_name);
  const observations = s(protocol.observations);

  const statusConfig: Record<string, { label: string; bg: string; text: string }> = {
    pending: { label: 'Pendiente', bg: 'bg-yellow-100', text: 'text-yellow-800' },
    in_progress: { label: 'En Proceso', bg: 'bg-blue-100', text: 'text-blue-800' },
    completed: { label: 'Completado', bg: 'bg-green-100', text: 'text-green-800' },
    cancelled: { label: 'Cancelado', bg: 'bg-red-100', text: 'text-red-800' },
  };
  const currentStatus = statusConfig[protocol.status] || { label: statusText, bg: 'bg-gray-100', text: 'text-gray-800' };

  const allResults: LabResult[] = protocol.results || [];
  const sectionObs: Record<string, LabResult> = {};
  const results = allResults.filter(r => {
    if (r.parameter_code === '_OBSERVACIONES') { sectionObs[r.test_type] = r; return false; }
    return true;
  });
  const testTypes = [...new Set(referenceValues.map(r => String(r.test_type)))];

  // Group results by test_type
  const grouped: Record<string, LabResult[]> = {};
  for (const r of results) {
    const tt = String(r.test_type);
    if (!grouped[tt]) grouped[tt] = [];
    grouped[tt].push(r);
  }
  const sectionKeys = Object.keys(grouped);

  // Handlers
  const changeStatus = async (st: LabProtocolStatus) => {
    try { await updateStatusMutation.mutateAsync({ id: protocol.id, status: st }); refetch(); }
    catch (e) { console.error(e); }
  };

  const loadTestType = async (tt: LabTestType) => {
    const refs = referenceValues.filter(r => r.test_type === tt);
    if (refs.length === 0) return;
    const existing = results.map(r => r.parameter_code);
    const newRefs = refs.filter(r => !existing.includes(r.parameter_code));
    if (newRefs.length === 0) { alert('Ya estan todos cargados'); return; }
    const data: LabResultCreate[] = newRefs.map(r => ({
      test_type: r.test_type, parameter_code: r.parameter_code,
      parameter_name: r.parameter_name, reference_min: r.reference_min,
      reference_max: r.reference_max, unit: r.unit || '',
    }));
    try { await createResultsBulkMutation.mutateAsync({ protocolId: protocol.id, results: data }); refetch(); }
    catch (e) { console.error(e); }
  };

  const updateResult = async (r: LabResult, val: string) => {
    const num = parseFloat(val);
    try {
      await updateResultMutation.mutateAsync({
        id: r.id, protocolId: protocol.id,
        data: { result_value: val, result_numeric: isNaN(num) ? undefined : num },
      });
      refetch();
    } catch (e) { console.error(e); }
  };

  const deleteResult = async (rid: number) => {
    if (!confirm('Eliminar?')) return;
    try { await deleteResultMutation.mutateAsync({ id: rid, protocolId: protocol.id }); refetch(); }
    catch (e) { console.error(e); }
  };

  const addResult = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createResultMutation.mutateAsync({ protocolId: protocol.id, data: resultForm });
      setShowAddResult(false);
      setResultForm({ test_type: 'hemograma', parameter_code: '', parameter_name: '', result_value: '', result_numeric: undefined, reference_min: undefined, reference_max: undefined, unit: '', notes: '' });
      refetch();
    } catch (err) { console.error(err); }
  };

  const downloadPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = 210;
      const marginL = 15;
      const marginR = 15;
      const contentW = pageW - marginL - marginR;
      let y = 20;

      // Header
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Resultados de Laboratorio', pageW / 2, y, { align: 'center' });
      y += 10;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Protocolo: ${protocolNumber}`, pageW / 2, y, { align: 'center' });
      y += 6;
      pdf.text(`Fecha: ${protocolDate}`, pageW / 2, y, { align: 'center' });
      y += 10;

      // Line separator
      pdf.setDrawColor(200);
      pdf.line(marginL, y, pageW - marginR, y);
      y += 8;

      // Patient info
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Paciente', marginL, y);
      y += 7;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const patientInfo = [
        ['Nombre', patientName, 'Especie', patientSpecies],
        ['Propietario', ownerName, 'Telefono', ownerPhone],
        ['Veterinario', vetName, 'Clinica', clinicName],
      ];
      for (const row of patientInfo) {
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${row[0]}:`, marginL, y);
        pdf.setFont('helvetica', 'normal');
        pdf.text(String(row[1]), marginL + 30, y);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${row[2]}:`, marginL + contentW / 2, y);
        pdf.setFont('helvetica', 'normal');
        pdf.text(String(row[3]), marginL + contentW / 2 + 25, y);
        y += 5;
      }
      if (observations !== '-') {
        y += 2;
        pdf.setFont('helvetica', 'bold');
        pdf.text('Observaciones:', marginL, y);
        pdf.setFont('helvetica', 'normal');
        pdf.text(String(observations), marginL + 30, y);
        y += 5;
      }
      y += 5;

      // Results by section
      for (const tt of sectionKeys) {
        // Check if we need a new page
        if (y > 250) { pdf.addPage(); y = 20; }

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 100, 150);
        pdf.text(testTypeLabel(tt).toUpperCase(), marginL, y);
        pdf.setTextColor(0, 0, 0);
        y += 2;

        const tableRows = grouped[tt].map(r => {
          const val = r.result_value ? String(r.result_value) : '-';
          const unit = r.unit ? String(r.unit) : '';
          const ref = r.reference_min != null && r.reference_max != null
            ? `${s(r.reference_min)} - ${s(r.reference_max)}`
            : r.reference_text ? String(r.reference_text) : '-';
          return [String(r.parameter_name), val, unit, ref];
        });

        autoTable(pdf, {
          startY: y,
          margin: { left: marginL, right: marginR },
          head: [['Parametro', 'Resultado', 'Unidad', 'Referencia']],
          body: tableRows,
          styles: { fontSize: 9, cellPadding: 2 },
          headStyles: { fillColor: [0, 100, 150], textColor: 255, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 55 },
            1: { cellWidth: 30, halign: 'center' },
            2: { cellWidth: 25, halign: 'center' },
            3: { cellWidth: 50, halign: 'center' },
          },
        });

        y = (pdf as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 3;

        // Section observations
        const obs = sectionObs[tt];
        if (obs && obs.notes) {
          if (y > 270) { pdf.addPage(); y = 20; }
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'italic');
          pdf.text(`Obs: ${String(obs.notes)}`, marginL, y);
          pdf.setFont('helvetica', 'normal');
          y += 7;
        } else {
          y += 4;
        }
      }

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(150);
      const now = new Date().toLocaleString('es-AR');
      pdf.text(`Generado: ${now}`, pageW / 2, 290, { align: 'center' });
      pdf.setTextColor(0);

      pdf.save(`protocolo-${protocolNumber}.pdf`);
    } catch (e) { console.error('Error PDF:', e); alert('Error al generar PDF'); }
  };

  const sendWhatsApp = () => {
    const rawPhone = protocol.patient?.owner_phone
      || protocol.patient?.clinic?.phone
      || protocol.patient?.clinic?.contact_vet_phone
      || '';
    if (!rawPhone.trim()) {
      alert('El cliente no tiene telefono cargado. Agregalo en el paciente o la clinica.');
      return;
    }
    let phone = rawPhone.replace(/\D/g, '');
    if (phone.startsWith('54')) phone = phone.slice(2);
    if (phone.startsWith('9')) phone = phone.slice(1);
    if (phone.startsWith('0')) phone = phone.slice(1);
    phone = '549' + phone;

    let msg = `*Resultados de Laboratorio*\n`;
    msg += `Protocolo: ${protocolNumber} | Fecha: ${protocolDate}\n`;
    msg += `Paciente: ${patientName} (${patientSpecies})\n\n`;

    for (const tt of sectionKeys) {
      msg += `*${testTypeLabel(tt).toUpperCase()}*\n`;
      for (const r of grouped[tt]) {
        const val = r.result_value ? String(r.result_value) : '-';
        const unit = r.unit ? String(r.unit) : '';
        const ref = r.reference_min != null && r.reference_max != null
          ? `(${s(r.reference_min)}-${s(r.reference_max)})`
          : '';
        msg += `${String(r.parameter_name)}: ${val} ${unit} ${ref}\n`;
      }
      const obs = sectionObs[tt];
      if (obs && obs.notes) msg += `_Obs: ${String(obs.notes)}_\n`;
      msg += '\n';
    }

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  const selectRef = (code: string) => {
    const ref = referenceValues.find(r => r.parameter_code === code);
    if (ref) setResultForm({ ...resultForm, test_type: ref.test_type, parameter_code: ref.parameter_code, parameter_name: ref.parameter_name, reference_min: ref.reference_min, reference_max: ref.reference_max, unit: ref.unit || '' });
  };

  const saveObs = async (tt: LabTestType, value: string) => {
    const existing = sectionObs[tt];
    try {
      if (existing) {
        if (value !== existing.notes) {
          await updateResultMutation.mutateAsync({ id: existing.id, protocolId: protocol.id, data: { notes: value } });
          refetch();
        }
      } else if (value.trim()) {
        await createResultMutation.mutateAsync({ protocolId: protocol.id, data: { test_type: tt, parameter_code: '_OBSERVACIONES', parameter_name: 'Observaciones', notes: value } });
        refetch();
      }
    } catch (e) { console.error(e); }
  };

  const toggleSection = useCallback((tt: string) => {
    setCollapsedSections(prev => ({ ...prev, [tt]: !prev[tt] }));
  }, []);

  // Auto-trigger action from query params (e.g. ?action=whatsapp from protocols list)
  useEffect(() => {
    const action = pendingAction.current;
    if (!action) return;
    pendingAction.current = null;
    setSearchParams({}, { replace: true });
    if (action === 'whatsapp') setTimeout(() => sendWhatsApp(), 100);
    if (action === 'pdf') setTimeout(() => downloadPDF(), 100);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/lab/protocols')} className="p-2 hover:bg-gray-100 rounded-lg">
          {"\u2190 Volver"}
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{"Protocolo "}{protocolNumber}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-gray-600">{"Fecha: "}{protocolDate}</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${currentStatus.bg} ${currentStatus.text}`}>
              {currentStatus.label}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {protocol.status === 'completed' ? (
            <button onClick={downloadPDF} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">{"Descargar PDF"}</button>
          ) : null}
          {protocol.status === 'completed' ? (
            <button onClick={sendWhatsApp} className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600">{"Enviar WhatsApp"}</button>
          ) : null}
          {protocol.status === 'pending' ? (
            <button onClick={() => changeStatus('in_progress')} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">{"Marcar En Proceso"}</button>
          ) : null}
          {protocol.status !== 'completed' ? (
            <button onClick={() => changeStatus('completed')} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">{"Guardar y Completar"}</button>
          ) : null}
        </div>
      </div>

      <div ref={printRef}>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="font-semibold mb-3">{"Paciente"}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <div><span className="text-gray-500">{"Nombre: "}</span>{patientName}</div>
          <div><span className="text-gray-500">{"Especie: "}</span>{patientSpecies}</div>
          <div><span className="text-gray-500">{"Dueno: "}</span>{ownerName}</div>
          <div><span className="text-gray-500">{"Telefono: "}</span>{ownerPhone}</div>
          <div><span className="text-gray-500">{"Veterinario: "}</span>{vetName}</div>
          <div><span className="text-gray-500">{"Clinica: "}</span>{clinicName}</div>
        </div>
        {observations !== '-' ? (
          <div className="mt-3 pt-3 border-t text-sm">
            <span className="text-gray-500">{"Obs: "}</span>{observations}
          </div>
        ) : null}
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold">{"Resultados"}</h2>
          <div className="flex gap-2 flex-wrap">
            {testTypes.length > 0 ? testTypes.map(tt => (
              <button key={tt} onClick={() => loadTestType(tt as LabTestType)} disabled={createResultsBulkMutation.isPending} className="px-3 py-1 border border-lab-600 text-lab-600 rounded text-sm disabled:opacity-50">
                {"Cargar "}{testTypeLabel(tt)}
              </button>
            )) : (
              <button onClick={async () => { await seedCanineMutation.mutateAsync(); window.location.reload(); }} disabled={seedCanineMutation.isPending} className="px-3 py-1 bg-green-600 text-white rounded text-sm disabled:opacity-50">
                {"Cargar Plantillas"}
              </button>
            )}
            <button onClick={() => setShowAddResult(true)} className="px-3 py-1 bg-lab-600 text-white rounded text-sm">
              {"+ Manual"}
            </button>
          </div>
        </div>

        {sectionKeys.length === 0 ? (
          <p className="text-center text-gray-400 py-4">{"Sin resultados"}</p>
        ) : (
          <div className="space-y-3">
            {sectionKeys.map(tt => {
              const isCollapsed = collapsedSections[tt];
              const filledCount = grouped[tt].filter(r => r.result_value).length;
              const totalCount = grouped[tt].length;
              return (
                <div key={tt} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleSection(tt)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-lab-50 hover:bg-lab-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-lab-600 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}>{"▶"}</span>
                      <span className="font-semibold text-lab-700 uppercase text-sm">{testTypeLabel(tt)}</span>
                      <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full">
                        {String(filledCount)}{" / "}{String(totalCount)}{" completados"}
                      </span>
                    </div>
                  </button>
                  {!isCollapsed ? (
                    <div>
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left">{"Parametro"}</th>
                            <th className="px-3 py-2 text-left">{"Resultado"}</th>
                            <th className="px-3 py-2 text-left">{"Ref"}</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {grouped[tt].map(r => (
                            <tr key={r.id}>
                              <td className="px-3 py-2 font-medium">{s(r.parameter_name)}</td>
                              <td className="px-3 py-2">
                                <input type="text" defaultValue={s(r.result_value)} onBlur={e => { if (e.target.value !== r.result_value) updateResult(r, e.target.value); }} className="w-20 px-1 py-0.5 border rounded text-center" />
                                {r.unit ? <span className="ml-1 text-gray-500">{s(r.unit)}</span> : null}
                              </td>
                              <td className="px-3 py-2 text-gray-500">
                                {r.reference_min != null && r.reference_max != null ? `${s(r.reference_min)} - ${s(r.reference_max)}` : s(r.reference_text)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button onClick={() => deleteResult(r.id)} className="text-red-500 text-xs">{"eliminar"}</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="px-4 py-2 border-t bg-gray-50">
                        <textarea
                          defaultValue={sectionObs[tt]?.notes || ''}
                          onBlur={e => saveObs(tt as LabTestType, e.target.value)}
                          placeholder={"Observaciones de " + testTypeLabel(tt) + "..."}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-lab-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>

      {showAddResult ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold mb-4">{"Agregar Resultado"}</h2>
            <form onSubmit={addResult} className="space-y-3">
              {referenceValues.length > 0 ? (
                <select onChange={e => selectRef(e.target.value)} className="w-full px-3 py-2 border rounded">
                  <option value="">{"Seleccionar parametro..."}</option>
                  {referenceValues.map(ref => (
                    <option key={ref.id} value={s(ref.parameter_code)}>
                      {s(ref.parameter_name)}
                    </option>
                  ))}
                </select>
              ) : null}
              <div className="grid grid-cols-2 gap-3">
                <select required value={s(resultForm.test_type)} onChange={e => setResultForm({ ...resultForm, test_type: e.target.value as LabTestType })} className="px-3 py-2 border rounded">
                  <option value="hemograma">{"Hemograma"}</option>
                  <option value="bioquimica">{"Bioquimica"}</option>
                  <option value="orina">{"Orina"}</option>
                  <option value="coprologia">{"Coprologia"}</option>
                  <option value="otros">{"Otros"}</option>
                </select>
                <input type="text" required placeholder="Codigo" value={s(resultForm.parameter_code)} onChange={e => setResultForm({ ...resultForm, parameter_code: e.target.value })} className="px-3 py-2 border rounded" />
              </div>
              <input type="text" required placeholder="Nombre parametro" value={s(resultForm.parameter_name)} onChange={e => setResultForm({ ...resultForm, parameter_name: e.target.value })} className="w-full px-3 py-2 border rounded" />
              <div className="grid grid-cols-3 gap-3">
                <input type="text" placeholder="Resultado" value={s(resultForm.result_value || '')} onChange={e => { const v = e.target.value; const n = parseFloat(v); setResultForm({ ...resultForm, result_value: v, result_numeric: isNaN(n) ? undefined : n }); }} className="px-3 py-2 border rounded" />
                <input type="number" step="any" placeholder="Ref min" value={resultForm.reference_min ?? ''} onChange={e => setResultForm({ ...resultForm, reference_min: e.target.value ? parseFloat(e.target.value) : undefined })} className="px-3 py-2 border rounded" />
                <input type="number" step="any" placeholder="Ref max" value={resultForm.reference_max ?? ''} onChange={e => setResultForm({ ...resultForm, reference_max: e.target.value ? parseFloat(e.target.value) : undefined })} className="px-3 py-2 border rounded" />
              </div>
              <input type="text" placeholder="Unidad (ej: mg/dl)" value={s(resultForm.unit || '')} onChange={e => setResultForm({ ...resultForm, unit: e.target.value })} className="w-full px-3 py-2 border rounded" />
              <div className="flex justify-end gap-3 pt-3">
                <button type="button" onClick={() => setShowAddResult(false)} className="px-4 py-2 border rounded">{"Cancelar"}</button>
                <button type="submit" disabled={createResultMutation.isPending} className="px-4 py-2 bg-lab-600 text-white rounded disabled:opacity-50">
                  {createResultMutation.isPending ? 'Guardando...' : 'Agregar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
